// v3 calculator store (module-scope zustand, root CLAUDE.md "Zustand for Module-Scope Client State").
//
// Holds three things:
//   1. flow state   - which path, which step, which steps were opened, which account / demo mode;
//   2. the v3 sizing state, field-for-field the `computeCalculator` input (deal, selected, tools,
//      customTools), so `sizingOf(state)` feeds the engine with no mapping;
//   3. `epoch`, bumped whenever the sizing is replaced wholesale (`hydrate`, `reset`). Editing
//      surfaces pass it as the `useDraftState` seed key, so they reseed on a hydrate and only then.
//
// Deliberately NOT here (later stages): autosave, Salesforce restore, share-link loading. Those
// call `hydrate` and read `sizingOf`. Pure and testable: no React, no fetch, no assets.

import { create } from 'zustand';
import { getSizedUseCase, type RecordLevel } from '../data/use-case-catalog';
import { DEFAULT_TOOLS16, TOOLS16 } from '../data/tools';
import {
  DEFAULT_DEAL,
  enabledMethods,
  orderSelection,
  type CalculatorInput,
  type DealEconomics,
  type UseCaseSelection,
} from '../engine/use-case-engine';
import type { Recheck } from '../engine/migrate-v3';
import type { CustomTool } from '../types';
import {
  ACCOUNT_STEP,
  DEAL_STEP,
  SELECTION_STEP,
  TITLE_STEP,
  buildSteps,
  stepNeighbors,
  stepIdForUseCase,
  type AccountSelection,
  type FlowPath,
  type Step,
  type StepModelInput,
} from '../flow/step-model';

/* ---------------------------------------------------------------------------- state */

export interface CalculatorState {
  /** null = the title card. */
  path: FlowPath | null;
  currentStepId: string;
  /** Step ids the SE has opened; feeds "not started" vs "in progress" in the step model. */
  visited: string[];
  /** null until the account step is answered; steps after it are locked until then. */
  account: AccountSelection | null;
  /** Bumped when the sizing is replaced wholesale. Use as a `useDraftState` seed key. */
  epoch: number;
  /**
   * The epoch `clearInputs` produced, else null. Autosave re-anchors on every epoch bump (a load is
   * never an edit); a clear IS an edit, so it skips the re-anchor when `clearedEpoch === epoch`.
   * Any later bump (hydrate, reset, demo) moves `epoch` past it, so no other action needs to set it.
   */
  clearedEpoch: number | null;

  // The v3 sizing state, exactly the `computeCalculator` input shape.
  deal: DealEconomics;
  selected: UseCaseSelection[];
  /** Tool consolidation checklist. null = untouched (default checklist applies, flagged Assumed). */
  tools: Record<string, number | ''> | null;
  customTools: CustomTool[] | null;

  /**
   * Migration re-check flags for answers a v2 -> v3 load could not carry over. Set by `hydrate`,
   * shown in the review list, dismissed one by one or automatically when the SE edits the flagged
   * answer. Not part of the sizing: autosave ignores it.
   */
  recheck: Recheck[];
}

export interface HydrateMeta {
  account?: AccountSelection | null;
  path?: FlowPath | null;
  /** Defaults to the use case selection step. */
  step?: string;
  /** Defaults to every non-terminal business-case step (restored data shows its real status). */
  visited?: string[];
  /** Open migration flags for the restored sizing. Defaults to none. */
  recheck?: Recheck[];
}

export interface CalculatorActionSet {
  // flow
  startBusinessCase: () => void;
  showTitle: () => void;
  goToStep: (id: string) => void;
  next: () => void;
  previous: () => void;
  // account
  chooseDemo: () => void;
  linkAccount: (
    account: { id: string; name: string },
    opportunity?: { id: string; name: string },
  ) => void;
  clearAccount: () => void;
  // selection
  selectUseCase: (id: string) => void;
  deselectUseCase: (id: string) => void;
  toggleUseCase: (id: string) => void;
  setRecordLevels: (id: string, levels: RecordLevel[]) => void;
  /** Calculation methods (GTM automation). At least one stays on; an empty pick keeps the first. */
  setMethods: (id: string, methods: string[]) => void;
  // answers
  setAnswer: (id: string, key: string, value: number | '') => void;
  resetAnswer: (id: string, key: string) => void;
  resetAnswers: (id: string) => void;
  setRealization: (id: string, value: number | '' | null) => void;
  setAttribution: (id: string, value: number | '' | null) => void;
  // deal + tools
  setDeal: (patch: Partial<DealEconomics>) => void;
  toggleTool: (toolId: string) => void;
  setToolSpend: (toolId: string, value: number | '') => void;
  setCustomTools: (tools: CustomTool[] | null) => void;
  /** Back to the untouched default checklist (flagged Assumed again). */
  resetTools: () => void;
  // re-check flags
  dismissRecheck: (flag: Recheck) => void;
  // lifecycle
  /** Every typed number back to Clay defaults; account, selection, step, names and tool checklist kept. */
  clearInputs: () => void;
  hydrate: (sizing: CalculatorInput, meta?: HydrateMeta) => void;
  reset: () => void;
}

/** The store holds state only; actions are module functions (below) so `setState(initialState, true)` resets cleanly. */
export type CalculatorStore = CalculatorState;

export const initialState: CalculatorState = {
  path: null,
  currentStepId: TITLE_STEP,
  visited: [TITLE_STEP],
  account: null,
  epoch: 0,
  clearedEpoch: null,
  deal: { ...DEFAULT_DEAL },
  selected: [],
  tools: null,
  customTools: null,
  recheck: [],
};

/** The use cases demo mode opens with: one per adoption story (data, inbound, cost). Real accounts start empty. */
export const DEMO_USE_CASE_IDS: readonly string[] = [
  'uc-crm-enrichment',
  'uc-inbound-speed-to-lead',
  'uc-tool-consolidation',
];

/* ------------------------------------------------------------------------ selectors */

/** The sizing slice in `computeCalculator` input shape. Builds a new object: select pieces, or wrap in `useShallow`. */
export function sizingOf(s: CalculatorState): CalculatorInput {
  return { deal: s.deal, selected: s.selected, tools: s.tools, customTools: s.customTools };
}

export function stepModelInputOf(s: CalculatorState): StepModelInput {
  return {
    path: s.path,
    account: s.account,
    deal: s.deal,
    selected: s.selected,
    tools: s.tools,
    customTools: s.customTools,
    visited: s.visited,
  };
}

export function stepsOf(s: CalculatorState): Step[] {
  return buildSteps(stepModelInputOf(s));
}

/** The use cases an account starts with: demo mode's three, a real account (or none yet) nothing. */
function startingSelectionFor(account: CalculatorState['account']): UseCaseSelection[] {
  return account?.kind === 'demo' ? orderSelection(DEMO_USE_CASE_IDS.map((id) => ({ id }))) : [];
}

/** True when anything differs from how this account or demo starts (what `clearInputs` would change). */
export function hasInputEditsOf(s: CalculatorState): boolean {
  const start = startingSelectionFor(s.account).map((x) => x.id);
  const ids = s.selected.map((x) => x.id);
  if (ids.length !== start.length || ids.some((id, i) => id !== start[i])) return true;
  if (s.tools !== null || s.customTools !== null) return true;
  if (s.selected.some((x) => (x.recordLevels?.length ?? 0) > 0)) return true;
  // Methods compare normalised: an explicit "A only" is how a fresh selection already starts.
  if (
    s.selected.some((x) => {
      const uc = getSizedUseCase(x.id);
      const on = uc ? enabledMethods(uc, x.methods) : [];
      return on.length > 0 && (on.length !== 1 || on[0] !== uc?.methods[0].id);
    })
  )
    return true;
  const d = s.deal;
  if (d.acv !== DEFAULT_DEAL.acv || d.winRate !== DEFAULT_DEAL.winRate) return true;
  if (d.contractEstimate !== DEFAULT_DEAL.contractEstimate || d.currentAnnualPipeline !== DEFAULT_DEAL.currentAnnualPipeline) return true;
  const answered = s.selected.some(
    (x) => Object.keys(x.answers ?? {}).length > 0 || (x.realization ?? null) !== null || (x.attribution ?? null) !== null,
  );
  return answered;
}

export function selectedIdsOf(s: CalculatorState): string[] {
  return s.selected.map((x) => x.id);
}

/* ------------------------------------------------------------------------- helpers */

const withVisited = (visited: string[], id: string) => (visited.includes(id) ? visited : [...visited, id]);

function patchSelection(
  selected: UseCaseSelection[],
  id: string,
  f: (s: UseCaseSelection) => UseCaseSelection,
): UseCaseSelection[] {
  return selected.map((s) => (s.id === id ? f(s) : s));
}

/** `{ a, b }` minus one key, without the unused-var lint noise of destructuring it away. */
function omitKey<T extends Record<string, unknown>>(obj: T, key: string): T {
  const copy = { ...obj };
  delete copy[key];
  return copy;
}

const TOOLS_USE_CASE = 'uc-tool-consolidation';

/** Drops the flags that belong to one edited slot (use case + key); `useCaseId` null = deal-level. */
function clearRecheck(recheck: Recheck[], useCaseId: string | null, key: string): Recheck[] {
  const next = recheck.filter((r) => !(r.useCaseId === useCaseId && r.key === key));
  return next.length === recheck.length ? recheck : next;
}

/* --------------------------------------------------------------------------- store */

export const useCalculatorStore = create<CalculatorStore>(() => ({
  ...initialState,
  deal: { ...DEFAULT_DEAL },
}));

const set = useCalculatorStore.setState;
const get = useCalculatorStore.getState;

/** Every state change goes through here. Not hooks: safe to call from handlers, effects and tests. */
export const calculatorActions: CalculatorActionSet = {

  /* flow ------------------------------------------------------------------------ */

  // "Build a business case" always opens the account step, whatever step the case was last on. Only
  // the step changes: the account, picks and answers stay.
  startBusinessCase: () =>
    set((s) => ({ path: 'business-case', currentStepId: ACCOUNT_STEP, visited: withVisited(s.visited, ACCOUNT_STEP) })),

  // Back to the title card. The case is kept.
  showTitle: () =>
    set((s) => ({
      path: null,
      currentStepId: TITLE_STEP,
      visited: withVisited(s.visited, TITLE_STEP),
    })),

  goToStep: (id) => {
    const step = stepsOf(get()).find((x) => x.id === id);
    if (!step || step.locked) return;
    set((s) => ({ currentStepId: id, visited: withVisited(s.visited, id) }));
  },

  next: () => {
    const { next } = stepNeighbors(stepsOf(get()), get().currentStepId);
    if (next && !next.locked) calculatorActions.goToStep(next.id);
  },

  previous: () => {
    const s = get();
    const { current, previous } = stepNeighbors(stepsOf(s), s.currentStepId);
    if (previous) calculatorActions.goToStep(previous.id);
    // The first business-case step steps back to the title card (path choice).
    else if (current && s.path === 'business-case') calculatorActions.showTitle();
  },

  /* account --------------------------------------------------------------------- */

  // Demo mode opens on the three demo use cases at default numbers. Already in demo mode: untouched
  // (coming back via the logo keeps the SE's edits). From a real account: that account's sizing is
  // replaced, never carried into the demo. A case loaded before any account (share link) is kept.
  chooseDemo: () =>
    set((s) => {
      if (s.account?.kind === 'demo') return s;
      if (s.account === null && s.selected.length > 0) return { account: { kind: 'demo' } };
      return {
        account: { kind: 'demo' },
        deal: { ...DEFAULT_DEAL },
        selected: startingSelectionFor({ kind: 'demo' }),
        tools: null,
        customTools: null,
        recheck: [],
        epoch: s.epoch + 1,
      };
    }),

  // A real account starts with nothing selected (its saved case, if any, is restored right after by
  // `hydrate`). Leaving demo mode for an account drops the demo sizing so it never leaks into one.
  linkAccount: (account, opportunity) =>
    set((s) => ({
      account: {
        kind: 'account',
        accountId: account.id,
        accountName: account.name,
        ...(opportunity ? { opportunityId: opportunity.id, opportunityName: opportunity.name } : {}),
      },
      ...(s.account?.kind === 'demo'
        ? { deal: { ...DEFAULT_DEAL }, selected: [], tools: null, customTools: null, recheck: [], epoch: s.epoch + 1 }
        : {}),
    })),

  clearAccount: () => set({ account: null }),

  /* selection ------------------------------------------------------------------- */

  selectUseCase: (id) =>
    set((s) => {
      if (!getSizedUseCase(id) || s.selected.some((x) => x.id === id)) return s;
      return { selected: orderSelection([...s.selected, { id }]) };
    }),

  deselectUseCase: (id) => set((s) => ({ selected: s.selected.filter((x) => x.id !== id) })),

  toggleUseCase: (id) => {
    if (get().selected.some((x) => x.id === id)) calculatorActions.deselectUseCase(id);
    else calculatorActions.selectUseCase(id);
  },

  setRecordLevels: (id, levels) =>
    set((s) => {
      const uc = getSizedUseCase(id);
      if (!uc) return s;
      // Catalog order, only levels the use case offers; the engine falls back to the first if none.
      const next = uc.recordLevels.filter((l) => levels.includes(l));
      return { selected: patchSelection(s.selected, id, (x) => ({ ...x, recordLevels: next })) };
    }),

  setMethods: (id, methods) =>
    set((s) => {
      const uc = getSizedUseCase(id);
      if (!uc || uc.methods.length === 0) return s;
      // Catalog order, only methods the use case offers, never none: the first is the fallback.
      const next = enabledMethods(uc, methods);
      return { selected: patchSelection(s.selected, id, (x) => ({ ...x, methods: next })) };
    }),

  /* answers --------------------------------------------------------------------- */

  setAnswer: (id, key, value) =>
    set((s) => ({
      selected: patchSelection(s.selected, id, (x) => ({ ...x, answers: { ...x.answers, [key]: value } })),
      recheck: clearRecheck(s.recheck, id, key),
    })),

  resetAnswer: (id, key) =>
    set((s) => ({
      selected: patchSelection(s.selected, id, (x) => ({ ...x, answers: omitKey(x.answers ?? {}, key) })),
    })),

  resetAnswers: (id) =>
    set((s) => ({ selected: patchSelection(s.selected, id, (x) => ({ ...x, answers: {} })) })),

  setRealization: (id, value) =>
    set((s) => ({
      selected: patchSelection(s.selected, id, (x) => ({ ...x, realization: value })),
      recheck: clearRecheck(clearRecheck(s.recheck, id, 'realization'), id, 'factors'),
    })),

  setAttribution: (id, value) =>
    set((s) => ({
      selected: patchSelection(s.selected, id, (x) => ({ ...x, attribution: value })),
      recheck: clearRecheck(clearRecheck(s.recheck, id, 'attribution'), id, 'factors'),
    })),

  /* deal + tools ---------------------------------------------------------------- */

  setDeal: (patch) =>
    set((s) => ({
      deal: { ...s.deal, ...patch },
      recheck: Object.keys(patch).reduce((acc, k) => clearRecheck(acc, null, k), s.recheck),
    })),

  toggleTool: (toolId) =>
    set((s) => {
      const base = s.tools ?? DEFAULT_TOOLS16; // first touch materialises the default checklist
      const recheck = clearRecheck(s.recheck, TOOLS_USE_CASE, 'tools');
      if (toolId in base) return { tools: omitKey({ ...base }, toolId), recheck };
      const def = TOOLS16.find((t) => t.id === toolId)?.def;
      return def === undefined ? s : { tools: { ...base, [toolId]: def }, recheck };
    }),

  setToolSpend: (toolId, value) =>
    set((s) => ({
      tools: { ...(s.tools ?? DEFAULT_TOOLS16), [toolId]: value },
      recheck: clearRecheck(s.recheck, TOOLS_USE_CASE, 'tools'),
    })),

  setCustomTools: (tools) =>
    set((s) => ({ customTools: tools, recheck: clearRecheck(s.recheck, TOOLS_USE_CASE, 'tools') })),

  resetTools: () => set({ tools: null, customTools: null }),

  /* re-check flags -------------------------------------------------------------- */

  dismissRecheck: (flag) =>
    set((s) => ({ recheck: s.recheck.filter((r) => r !== flag) })),

  /* lifecycle ------------------------------------------------------------------- */

  // Back to how this account or demo starts: default numbers, default selection (demo: the three
  // demo use cases; a real account: none), default tool checklist, no migration flags. Kept: the
  // account/demo choice and the step, unless that step was a use case page that no longer exists.
  clearInputs: () =>
    set((s) => {
      const selected = startingSelectionFor(s.account);
      const epoch = s.epoch + 1;
      const next = { ...s, selected, tools: null, customTools: null };
      const stepStillThere = stepsOf(next).some((x) => x.id === s.currentStepId && !x.locked);
      const currentStepId = stepStillThere ? s.currentStepId : SELECTION_STEP;
      const live = new Set(stepsOf(next).map((x) => x.id));
      return {
        deal: { ...DEFAULT_DEAL },
        selected,
        tools: null,
        customTools: null,
        recheck: [],
        currentStepId,
        visited: withVisited(s.visited.filter((id) => live.has(id)), currentStepId),
        epoch,
        clearedEpoch: epoch,
      };
    }),

  hydrate: (sizing, meta = {}) =>
    set((s) => {
      const selected = orderSelection([...sizing.selected]);
      const path = meta.path === undefined ? 'business-case' : meta.path;
      const defaultVisited =
        path === 'business-case'
          ? [ACCOUNT_STEP, SELECTION_STEP, DEAL_STEP, ...selected.map((x) => stepIdForUseCase(x.id))]
          : [TITLE_STEP];
      return {
        path,
        currentStepId: meta.step ?? (path === 'business-case' ? SELECTION_STEP : TITLE_STEP),
        visited: meta.visited ?? defaultVisited,
        account: meta.account === undefined ? s.account : meta.account,
        epoch: s.epoch + 1,
        // A payload that predates the default (null / missing pipeline) loads as the default.
        deal: {
          ...DEFAULT_DEAL,
          ...sizing.deal,
          currentAnnualPipeline: sizing.deal.currentAnnualPipeline ?? DEFAULT_DEAL.currentAnnualPipeline,
        },
        selected,
        tools: sizing.tools ?? null,
        customTools: sizing.customTools ?? null,
        recheck: meta.recheck ?? [],
      };
    }),

  reset: () => set((s) => ({ ...initialState, deal: { ...DEFAULT_DEAL }, epoch: s.epoch + 1 })),
};
